import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { addAudioToVideo, checkTaskStatus, getDefaultAudioUrl } from '../../services/videoApi';
import { 
    Upload, Download, Loader2, AlertCircle, 
    CheckCircle, XCircle, FileUp, Music 
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const AddAudioToVideo = () => {
    const { t } = useTranslation();
    const { user, setCredits} = useAuth();
    
    // File State
    const [videoFile, setVideoFile] = useState(null);
    const [audioFile, setAudioFile] = useState(null);
    const [previewDefaultUrl, setPreviewDefaultUrl] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    
    // Params State
    const [volume, setVolume] = useState(0.3);
    const [repeatAudio, setRepeatAudio] = useState(true);
    const [error, setError] = useState(null);
    
    // Process State
    const [isProcessing, setIsProcessing] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [progressStep, setProgressStep] = useState('');
    const [progressValue, setProgressValue] = useState(0);
    const [resultUrl, setResultUrl] = useState(null);
    
    const pollingIntervalRef = useRef(null);

    useEffect(() => {
        const fetchUrl = async () => {
            try {
                const url = await getDefaultAudioUrl();
                setPreviewDefaultUrl(url);
            } catch (err) {
                console.error("Could not load default audio preview:", err);
            }
        };
        fetchUrl();
    }, []);

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, [previewUrl]);

    // Polling Logic
    useEffect(() => {
        if (taskId && isProcessing) {
            pollingIntervalRef.current = setInterval(async () => {
                try {
                    const data = await checkTaskStatus(taskId);
                    
                    if (data.state === 'SUCCESS' || data.status === 'SUCCESS') {
                        clearInterval(pollingIntervalRef.current);
                        
                        const payload = data.result; 

                        if (payload.result && payload.result.download_url) {
                            setResultUrl(payload.result.download_url);
                            
                            if (payload.new_credits !== undefined) {
                                setCredits(payload.new_credits);
                            }
                            
                            setProgressValue(100);
                            setIsProcessing(false);
                        } else {
                            console.error("Task succeeded but no URL:", payload);
                            setError(t('addAudioVideo.errors.generic'));
                            setIsProcessing(false);
                        }

                    } else if (data.state === 'FAILURE' || data.status === 'FAILURE') {
                        clearInterval(pollingIntervalRef.current);
                        setError(data.error || t('addAudioVideo.errors.generic'));
                        setIsProcessing(false);
                    } else {
                        if (data.info) {
                            if (data.info.step) setProgressStep(data.info.step);
                            if (data.info.progress) setProgressValue(data.info.progress);
                        }
                    }
                } catch (err) {
                    clearInterval(pollingIntervalRef.current);
                    setError(err.message);
                    setIsProcessing(false);
                }
            }, 2500);
        }
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, [taskId, isProcessing, setCredits, t]);

    const handleVideoChange = (e) => {
        const selected = e.target.files[0];
        if (selected) {
            if (selected.size > 2000 * 1024 * 1024) { 
                setError(t('addAudioVideo.errors.tooLarge')); 
                return; 
            }
            setVideoFile(selected);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(selected));
            setError(null);
            setResultUrl(null);
        }
    };

    const handleClear = () => {
        setVideoFile(null);
        setAudioFile(null);
        setResultUrl(null);
        setTaskId(null);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
    };

    const handleProcess = async () => {
        if (!videoFile) {
            setError(t('addAudioVideo.errors.missingVideo'));
            return;
        }

        setIsProcessing(true);
        setError(null);
        try {
            const response = await addAudioToVideo(
                videoFile, 
                audioFile,  // Can be null, backend handles it
                volume, 
                repeatAudio
            );
            setTaskId(response.task_id);
        } catch (err) {
            setError(err.message);
            setIsProcessing(false);
        }
    };

    const getProgressLabel = (step) => {
        const map = {
            'downloading': t('addAudioVideo.steps.downloading'),
            'processing_audio': t('addAudioVideo.steps.processing_audio'),
            'encoding': t('addAudioVideo.steps.encoding'),
            'uploading': t('addAudioVideo.steps.uploading')
        };
        return map[step] || t('addAudioVideo.processing');
    };

    const UploadView = () => (
        <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold mb-2 text-brand-orange">{t('addAudioVideo.title')}</h2>
            <p className="mt-2 text-brand-dark">{t('addAudioVideo.description')}</p>
            
            <div className="mt-6 space-y-6">
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Video Upload */}
                    <div>
                        <label className="block text-sm font-medium mb-2 text-brand-dark">{t('addAudioVideo.videoLabel')}</label>
                        <div className="flex items-center p-4 border-2 border-dashed border-slate-400 rounded-md bg-slate-50">
                            <FileUp className="h-6 w-6 text-slate-500" />
                            <label htmlFor="video-upload" className="ml-4 text-sm text-brand-orange font-semibold cursor-pointer hover:text-orange-600 truncate">
                                {videoFile ? videoFile.name : t('addAudioVideo.selectVideo')}
                                <input id="video-upload" type="file" className="sr-only" onChange={handleVideoChange} accept=".mp4,.avi,.mov" />
                            </label>
                        </div>
                    </div>

                    {/* Audio Upload - Optional */}
                    <div>
                        <label className="block text-sm font-medium mb-2 text-brand-dark">
                            {t('addAudioVideo.audioLabel')} <span className="text-slate-500 text-xs">({t('addAudioVideo.optional')})</span>
                        </label>
                        <div className="flex items-center p-4 border-2 border-dashed border-slate-400 rounded-md bg-slate-50">
                            <Music className="h-6 w-6 text-slate-500" />
                            <label htmlFor="audio-upload" className="ml-4 text-sm text-brand-orange font-semibold cursor-pointer hover:text-orange-600 truncate">
                                {audioFile ? audioFile.name : t('addAudioVideo.selectAudio')}
                                <input id="audio-upload" type="file" className="sr-only" onChange={(e) => setAudioFile(e.target.files[0])} accept=".mp3,.wav,.ogg,.m4a" />
                            </label>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{t('addAudioVideo.defaultHint')}</p>
                    </div>
                </div>

                {/* Default Audio Preview */}
                <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
                    <h3 className="font-semibold text-brand-dark mb-3">{t('addAudioVideo.defaultPreviewTitle')}</h3>
                    {previewDefaultUrl ? (
                        <audio controls className="w-full" src={previewDefaultUrl}>
                            {t('addAudioVideo.browserSupport')}
                        </audio>
                    ) : (
                        <div className="text-sm text-slate-400 flex items-center justify-center py-2">
                            <Loader2 className="animate-spin mr-2 h-4 w-4" /> {t('addAudioVideo.loadingPreview')}
                        </div>
                    )}
                </div>

                {/* Settings */}
                <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
                    <h3 className="font-semibold text-brand-dark mb-4">{t('addAudioVideo.settings')}</h3>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                {t('addAudioVideo.volume')}: {(volume * 100).toFixed(0)}%
                            </label>
                            <input 
                                type="range" min="0" max="1" step="0.1" 
                                value={volume} onChange={(e) => setVolume(parseFloat(e.target.value))}
                                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-orange"
                            />
                        </div>

                        <div className="flex items-center">
                            <input 
                                id="repeat-check" type="checkbox" 
                                checked={repeatAudio} onChange={(e) => setRepeatAudio(e.target.checked)}
                                className="w-4 h-4 text-brand-orange border-slate-300 rounded focus:ring-brand-orange"
                            />
                            <label htmlFor="repeat-check" className="ml-2 text-sm text-slate-700">
                                {t('addAudioVideo.repeatLabel')}
                            </label>
                        </div>
                    </div>
                </div>

                {error && <p className="text-red-500 flex items-center"><AlertCircle size={16} className="mr-2"/>{error}</p>}

                {previewUrl && (
                    <div className="rounded-lg overflow-hidden bg-black aspect-video border border-slate-300">
                        <video src={previewUrl} controls className="w-full h-full object-contain" />
                    </div>
                )}

                <button
                    onClick={handleProcess}
                    disabled={!videoFile || isProcessing}
                    className="w-full md:w-auto px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center"
                >
                    {isProcessing ? <><Loader2 className="animate-spin mr-2" /> {t('addAudioVideo.processing')}</> : t('addAudioVideo.processButton')}
                </button>

                {isProcessing && (
                    <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
                        <div className="flex justify-between text-sm font-medium text-slate-700 mb-2">
                            <span>{getProgressLabel(progressStep)}</span>
                            <span>{progressValue}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                            <div className="bg-brand-orange h-2.5 rounded-full transition-all duration-500" style={{ width: `${progressValue}%` }}></div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const ResultView = () => (
        <div className="max-w-4xl mx-auto text-center">
            <div className="bg-green-50 border border-green-200 p-4 rounded-md mb-8 flex items-center justify-center text-green-800">
                <CheckCircle className="mr-2" /> {t('addAudioVideo.success')}
            </div>
            <div className="rounded-lg overflow-hidden bg-black aspect-video mb-6 shadow-lg">
                <video src={resultUrl} controls className="w-full h-full object-contain" />
            </div>
            <a href={resultUrl} download="mixed_video.mp4" className="inline-flex items-center px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600">
                <Download className="mr-2" size={20} /> {t('addAudioVideo.download')}
            </a>
            <button onClick={handleClear} className="block w-full mt-6 text-slate-600 hover:text-brand-orange underline">{t('addAudioVideo.processAnother')}</button>
        </div>
    );

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? <SubscribePrompt /> : 
             resultUrl ? <ResultView /> : <UploadView />}
        </div>
    );
};

export default AddAudioToVideo;
